import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  formatDateForInput,
  getDateRangePreset,
  getDaysInMonth,
  getFirstDayOfMonth,
  getMonthName,
  isSameDay,
  getToday,
  DateRange,
} from '../lib/dateUtils';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (range: DateRange) => void;
  onApply: () => void;
}

export default function DateRangePicker({
  startDate,
  endDate,
  onChange,
  onApply,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [selectingStart, setSelectingStart] = useState(true);
  const [startMonth, setStartMonth] = useState(new Date().getMonth());
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(new Date().getMonth());
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handlePresetClick = (preset: string) => {
    const range = getDateRangePreset(preset);
    setTempStartDate(range.startDate);
    setTempEndDate(range.endDate);
    onChange(range);
  };

  const handleConfirm = () => {
    onChange({ startDate: tempStartDate, endDate: tempEndDate });
    onApply();
    setIsOpen(false);
  };

  const handleDateClick = (year: number, month: number, day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    if (selectingStart) {
      setTempStartDate(dateStr);
      setSelectingStart(false);
    } else {
      if (new Date(dateStr) >= new Date(tempStartDate)) {
        setTempEndDate(dateStr);
      } else {
        setTempStartDate(dateStr);
        setTempEndDate(tempStartDate);
      }
      setSelectingStart(true);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next', calendar: 'start' | 'end') => {
    if (calendar === 'start') {
      if (direction === 'prev') {
        if (startMonth === 0) {
          setStartMonth(11);
          setStartYear(startYear - 1);
        } else {
          setStartMonth(startMonth - 1);
        }
      } else {
        if (startMonth === 11) {
          setStartMonth(0);
          setStartYear(startYear + 1);
        } else {
          setStartMonth(startMonth + 1);
        }
      }
    } else {
      if (direction === 'prev') {
        if (endMonth === 0) {
          setEndMonth(11);
          setEndYear(endYear - 1);
        } else {
          setEndMonth(endMonth - 1);
        }
      } else {
        if (endMonth === 11) {
          setEndMonth(0);
          setEndYear(endYear + 1);
        } else {
          setEndMonth(endMonth + 1);
        }
      }
    }
  };

  const renderCalendar = (year: number, month: number, calendar: 'start' | 'end') => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const date = new Date(year, month, day);
      const isStart = dateStr === tempStartDate;
      const isEnd = dateStr === tempEndDate;
      const isInRange =
        new Date(dateStr) >= new Date(tempStartDate) && new Date(dateStr) <= new Date(tempEndDate);
      const isCurrentDay = isSameDay(date, getToday());

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateClick(year, month, day)}
          className={`h-8 w-8 rounded-full text-sm transition-colors ${
            isStart || isEnd
              ? 'bg-blue-600 text-white font-semibold'
              : isInRange
              ? 'bg-blue-100 text-blue-900'
              : isCurrentDay
              ? 'bg-gray-200 text-gray-900 font-medium'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => navigateMonth('prev', calendar)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="font-semibold text-gray-900">
            {getMonthName(month)} {year}
          </div>
          <button
            type="button"
            onClick={() => navigateMonth('next', calendar)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">{days}</div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span>
          {tempStartDate} → {tempEndDate}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 w-[800px]">
          <div className="flex">
            <div className="w-48 border-r border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Filters</h3>
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={() => handlePresetClick('today')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetClick('yesterday')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetClick('this_week')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  This Week
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetClick('last_week')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  Last Week
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetClick('last_7_days')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  Last 7 Days
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetClick('this_month')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  This Month
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetClick('last_month')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  Last Month
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetClick('this_year')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  This Year
                </button>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex">
                <div className="flex-1 border-r border-gray-200">
                  {renderCalendar(startYear, startMonth, 'start')}
                </div>
                <div className="flex-1">
                  {renderCalendar(endYear, endMonth, 'end')}
                </div>
              </div>

              <div className="border-t border-gray-200 p-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {selectingStart ? 'Select start date' : 'Select end date'}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
